import React from "react";
import { Check } from "lucide-react";
import Panel from "../Panel/Panel.jsx";
import { useApp } from "../../../hooks/useApp.js";
import { useTotals } from "../../../hooks/useTotals.js";

// steps/n come from Wizard.jsx's buildSteps(state) (stepConfig.js) - the
// single source of truth for the sequence, so this always matches what
// Wizard.jsx actually renders, including the amendment/VIN-correction pages
// that only exist for some filing types. Renders as a horizontal row (full
// width, above the step content) rather than a left-hand column - scrolls
// horizontally instead of wrapping so the connecting line between steps
// never breaks.
const Stepper = ({ steps, n, onNavigate }) => {
  const { state } = useApp();
  const t = useTotals();

  return (
    <Panel className="p-[16px_18px] mb-5">
      <div className="font-disp uppercase tracking-[.12em] text-[11px] text-[var(--muted)] mb-3">
        Your route
      </div>

      <div className="flex items-start overflow-x-auto">
        {steps.map((step, index) => {
          const position = index + 1;
          const done = step.isDone(state, t, position);
          const isCurrent = position === n;
          const isCompleted = position < n || (position <= state.maxStep && done);
          const clickable = position <= state.maxStep || position <= n;

          const numCls = isCurrent
            ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent-soft)]"
            : isCompleted
            ? "bg-[var(--secondary)] border-[var(--secondary)] text-white"
            : "border-[var(--line-2)] bg-[var(--panel)] text-[var(--dim)]";

          const textCls = isCurrent
            ? "text-[var(--ink)] font-semibold"
            : isCompleted
            ? "text-[var(--ink)]"
            : "text-[var(--dim)]";

          return (
            <React.Fragment key={step.key}>
              <div
                onClick={() => clickable && onNavigate(position)}
                className={[
                  "flex flex-col items-center text-center gap-1.5 px-2 flex-none w-[100px]",
                  clickable ? "cursor-pointer" : "cursor-not-allowed",
                ].join(" ")}
              >
                <span
                  className={[
                    "w-[30px] h-[30px] rounded-lg border-2 grid place-items-center font-disp font-semibold text-sm flex-none",
                    numCls,
                  ].join(" ")}
                >
                  {isCompleted ? <Check size={15} /> : position}
                </span>
                <div className={textCls}>
                  <b className="block text-[12.5px] font-semibold leading-tight">{step.label}</b>
                  <small className="block text-[var(--dim)] text-[10.5px] font-normal whitespace-nowrap">
                    {step.subFor(state, t, position)}
                  </small>
                </div>
              </div>

              {index < steps.length - 1 && (
                <div
                  className={[
                    "h-[2px] flex-1 min-w-[24px] mt-[14px] rounded-full",
                    isCompleted ? "bg-[var(--accent)]" : "bg-[var(--line-2)]",
                  ].join(" ")}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </Panel>
  );
};

export default Stepper;
